import { Service } from '@angular/core';
import { toast } from '@spartan-ng/brain/sonner';


export type ToastPosition =
    | 'top-left' | 'top-center' | 'top-right'
    | 'bottom-left' | 'bottom-center' | 'bottom-right';
export type ToastType = 'default' | 'success' | 'error' | 'warning' | 'info';

export interface ToastActionConfig<T = unknown> {
    label: string;
    /** The function to run when clicked. Can be sync or async, and can return a value. */
    run: () => T | Promise<T>;
    /** Called with whatever `run()` returns (or throws). Use this to get the result back in the parent. */
    onResult?: (result: T) => void;
    onError?: (error: unknown) => void;
}

export interface NotifyOptions {
    description?: string;
    duration?: number;
    position?: ToastPosition;
    closeButton?: boolean;
    dismissible?: boolean;
    id?: string | number;
    action?: ToastActionConfig;
    cancel?: ToastActionConfig<void>;
    onDismiss?: () => void;
    onAutoClose?: () => void;
}
export interface PromiseToastMessages<T> {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((error: unknown) => string);
}

@Service()
export class NotificationService {

    notify(message: string, type: ToastType = 'default', options: NotifyOptions = {}): string | number {
        const toastOptions = this.buildOptions(options);

        switch (type) {
            case 'success':
                return toast.success(message, toastOptions);
            case 'error':
                return toast.error(message, toastOptions);
            case 'warning':
                return toast.warning(message, toastOptions);
            case 'info':
                return toast.info(message, toastOptions);
            default:
                return toast(message, toastOptions);
        }
    }

    // success(message: string, options?: NotifyOptions) {
    //     return this.notify(message, 'success', options);
    // }

    // error(message: string, options?: NotifyOptions) {
    //     return this.notify(message, 'error', options);
    // }

    // warning(message: string, options?: NotifyOptions) {
    //     return this.notify(message, 'warning', options);
    // }

    // info(message: string, options?: NotifyOptions) {
    //     return this.notify(message, 'info', options);
    // }

    /** Wraps toast.promise — for async operations (API calls, syncs, etc.) */
    promise<T>(
        action: () => Promise<T>,
        messages: PromiseToastMessages<T>,
        options?: Omit<NotifyOptions, 'action' | 'cancel'>,
    ) {
        return toast.promise<T>(action, {
            loading: messages.loading,
            success: messages.success,
            error: messages.error,
            ...this.buildOptions(options ?? {}),
        });
    }

    dismiss(id?: string | number) {
        toast.dismiss(id);
    }

    /** Converts our ToastActionConfig into the shape sonner expects, wiring up run + onResult/onError. */
    private wrapAction<T>(action?: ToastActionConfig<T>) {
        if (!action) return undefined;

        return {
            label: action.label,
            onClick: async () => {
                try {
                    const result = await action.run();
                    action.onResult?.(result);
                } catch (err) {
                    if (action.onError) {
                        action.onError(err);
                    } else {
                        console.error('Toast action failed:', err);
                    }
                }
            },
        };
    }


    private buildOptions(options: NotifyOptions) {
        const { action, cancel, ...rest } = options;
        return {
            ...rest,
            action: this.wrapAction(action),
            cancel: this.wrapAction(cancel),
        };
    }
}
